import { useMemo, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormSwitch, FormCombobox } from '@/components/forms'
import { useUsers } from '@/hooks/api/useUsers'

/**
 * Notifications Tab - Email notification settings
 *
 * Includes:
 * - Notification triggers (outcome checkboxes)
 * - Recipient of notifications (user selector with API-based autocomplete)
 * - Include call transcript toggle
 * - Daily report toggle with schedule time and recipient
 */

const NOTIFICATION_OUTCOME_KEYS = [
  'business_follow_up',
  'caller_follow_up',
  'transferred',
  'information_provided',
  'completed',
  'no_action',
] as const

export function NotificationsTab() {
  const { t } = useTranslation()
  const { control, watch, setValue, getValues } = useFormContext()
  const notificationOutcomes = watch('notification_outcomes') || []
  const customerId = watch('customer_id')

  // Watch daily report enabled state
  const config = useWatch({ control, name: 'config' }) as Record<string, unknown> || {}
  const dailyReportEnabled = (config.daily_report as Record<string, unknown>)?.enabled as boolean

  // Map outcome keys to translated options
  const NOTIFICATION_OUTCOMES = useMemo(() => NOTIFICATION_OUTCOME_KEYS.map(key => ({
    value: key,
    label: t(`resources.assistants.notifications.outcomes.${key}`)
  })), [t])

  // Fetch users filtered by customer_id only (no partner users)
  // This matches v1 behavior: notification recipient should only show
  // users belonging to the customer the assistant is assigned to
  const { data: users = [], isLoading: usersLoading } = useUsers(
    customerId,
    !!customerId, // Only fetch when customer_id is available
    false // Don't include partner users - only customer users
  )

  // Map users to combobox options
  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id,
        label: `${user.first_name} ${user.last_name} (${user.email})`,
      })),
    [users]
  )

  const handleOutcomeChange = (outcomeId: string, checked: boolean) => {
    if (checked) {
      setValue('notification_outcomes', [...notificationOutcomes, outcomeId], {
        shouldDirty: true,
      })
    } else {
      setValue(
        'notification_outcomes',
        notificationOutcomes.filter((id: string) => id !== outcomeId),
        { shouldDirty: true }
      )
    }
  }

  // Track previous enabled state to detect when it changes to true
  const prevEnabledRef = useRef<boolean | undefined>(undefined)

  // Initialize daily report defaults when enabled for the first time
  useEffect(() => {
    const wasEnabled = prevEnabledRef.current
    prevEnabledRef.current = dailyReportEnabled

    // Only initialize when transitioning from false/undefined to true
    if (dailyReportEnabled && !wasEnabled && wasEnabled !== undefined) {
      const currentConfig = getValues('config') || {}
      const currentDailyReport = (currentConfig.daily_report || {}) as Record<string, unknown>
      
      // Only set defaults if schedule doesn't exist yet
      if (!currentDailyReport.schedule) {
        setValue('config', {
          ...currentConfig,
          daily_report: {
            ...currentDailyReport,
            enabled: true,
            schedule: { hour: 20, minute: 0 },
            user_id: currentDailyReport.user_id ?? null
          }
        }, { shouldDirty: true })
      }
    }
  }, [dailyReportEnabled, getValues, setValue])

  // Handle hour change for daily report schedule
  const handleHourChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const hour = Number(e.target.value)
    const currentConfig = getValues('config') || {}
    const currentSchedule = (currentConfig.daily_report as Record<string, unknown>)?.schedule as { hour: number; minute: number } | undefined
    setValue('config', {
      ...currentConfig,
      daily_report: {
        ...(currentConfig.daily_report || {}),
        schedule: { hour, minute: currentSchedule?.minute ?? 0 }
      }
    }, { shouldDirty: true })
  }, [getValues, setValue])

  // Handle minute change for daily report schedule (only 0, 15, 30, 45 allowed)
  const handleMinuteChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const minute = Number(e.target.value)
    const currentConfig = getValues('config') || {}
    const currentSchedule = (currentConfig.daily_report as Record<string, unknown>)?.schedule as { hour: number; minute: number } | undefined
    setValue('config', {
      ...currentConfig,
      daily_report: {
        ...(currentConfig.daily_report || {}),
        schedule: { hour: currentSchedule?.hour ?? 20, minute }
      }
    }, { shouldDirty: true })
  }, [getValues, setValue])

  // Get current schedule hour and minute
  const getScheduleHour = () => {
    const schedule = (config.daily_report as Record<string, unknown>)?.schedule as { hour: number; minute: number } | undefined
    return schedule?.hour ?? 20
  }

  const getScheduleMinute = () => {
    const schedule = (config.daily_report as Record<string, unknown>)?.schedule as { hour: number; minute: number } | undefined
    return schedule?.minute ?? 0
  }

  return (
    <div className="space-y-8">
      {/* Notification Settings - Triggers, Recipient, and Transcript */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div className="space-y-6">
          {/* Notification Triggers */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.notifications.triggers')}
            </h3>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.notifications.triggers_description')}
            </p>
            <div className="space-y-3">
              {NOTIFICATION_OUTCOMES.map((outcome) => (
                <label
                  key={outcome.value}
                  className="flex cursor-pointer items-center gap-3"
                >
                  <input
                    type="checkbox"
                    checked={notificationOutcomes.includes(outcome.value)}
                    onChange={(e) => handleOutcomeChange(outcome.value, e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800"
                  />
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {outcome.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Recipient of Notifications */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.notifications.recipient')}
            </h3>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.notifications.recipient_description')}
            </p>
            <FormCombobox
              name="notification_user"
              control={control}
              options={userOptions}
              placeholder={usersLoading ? t('resources.generic.loading') : t('resources.assistants.notifications.select_user')}
              isDisabled={!customerId || usersLoading}
            />
            {!customerId && (
              <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                {t('resources.assistants.notifications.select_customer_first')}
              </p>
            )}
          </div>

          {/* Include Transcript */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.notifications.include_transcript')}
            </h3>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.notifications.include_transcript_description')}
            </p>
            <FormSwitch name="config.notifications.include_transcript" control={control} />
          </div>
        </div>
      </div>

      {/* Daily Report */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.notifications.daily_report')}
            </h3>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.notifications.daily_report_description')}
            </p>
            <FormSwitch name="config.daily_report.enabled" control={control} />
          </div>

          {/* Daily Report Schedule - only shown when enabled */}
          {dailyReportEnabled && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('resources.assistants.notifications.daily_report_schedule')}
              </h3>
              <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                {t('resources.assistants.notifications.daily_report_schedule_description')}
              </p>
              <div className="flex items-center gap-2">
                {/* Hour selector */}
                <select
                  value={getScheduleHour()}
                  onChange={handleHourChange}
                  className="w-20 appearance-none rounded-lg border border-gray-300 bg-white bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_8px_center] bg-no-repeat py-2 pl-3 pr-8 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>
                      {String(i).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span className="text-gray-500 dark:text-gray-400">:</span>
                {/* Minute selector (only 0, 15, 30, 45) */}
                <select
                  value={getScheduleMinute()}
                  onChange={handleMinuteChange}
                  className="w-20 appearance-none rounded-lg border border-gray-300 bg-white bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:16px_16px] bg-[right_8px_center] bg-no-repeat py-2 pl-3 pr-8 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                >
                  <option value={0}>00</option>
                  <option value={15}>15</option>
                  <option value={30}>30</option>
                  <option value={45}>45</option>
                </select>
              </div>
            </div>
          )}

          {/* Daily Report Recipient - only shown when enabled */}
          {dailyReportEnabled && (
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('resources.assistants.notifications.daily_report_recipient')}
              </h3>
              <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                {t('resources.assistants.notifications.daily_report_recipient_description')}
              </p>
              <FormCombobox
                name="config.daily_report.user_id"
                control={control}
                options={userOptions}
                placeholder={usersLoading ? t('resources.generic.loading') : t('resources.assistants.notifications.select_user')}
                isDisabled={!customerId || usersLoading}
              />
              {!customerId && (
                <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                  {t('resources.assistants.notifications.select_customer_first')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
